import React, { useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, OrgUserOut, UserCredentialsOut } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { buildCredentialsMessage, userFullName } from './userManagementUtils';

type CredentialsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  user: OrgUserOut;
  credentials: UserCredentialsOut;
  isNewUser: boolean;
};

export const CredentialsDialog = ({ isOpen, onClose, user, credentials, isNewUser }: CredentialsDialogProps) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const message = buildCredentialsMessage(t, { user, credentials, studio: account.name, isNewUser });

  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message);
    setCopySnackbarOpen(true);
  };

  return (
    <Dialog open={isOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('users.credentials.title', { name: userFullName(user) })}</DialogTitle>
      <DialogContent>
        <Grid container flexDirection="column" gap={2}>
          <Alert severity="success">
            {t(isNewUser ? 'users.credentials.created' : 'users.credentials.reset', { firstName: user.first_name })}
          </Alert>
          <Grid container flexDirection="column" gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('users.credentials.message_label')}
            </Typography>
            <Typography
              variant="body2"
              className="ph-no-capture"
              sx={{
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
                bgcolor: 'mb.ground',
                border: 1,
                borderColor: 'mb.line',
                borderRadius: 1,
                p: 2,
              }}>
              {message}
            </Typography>
          </Grid>
          <Grid container>
            <Button variant="contained" startIcon={<ContentCopy />} sx={{ textTransform: 'none' }} onClick={handleCopy}>
              {t('users.credentials.copy')}
            </Button>
          </Grid>
          <Typography variant="caption" color="text.secondary">
            {t('users.credentials.note')}
          </Typography>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button sx={{ textTransform: 'none' }} onClick={onClose}>
          {t('close')}
        </Button>
      </DialogActions>
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setCopySnackbarOpen(false)}>
          {t('copiedToClipboard')}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
